/**
 * PF v2 file hub — kind #1: staging visuals.
 * ============================================================================
 *
 * The detached staging-visual upload subsystem, generalized onto the file hub.
 * Reproduces the old `adapters/staging/visualUpload/` behavior EXACTLY: same
 * FileAPI scope string (`PF_V2_STAGING_VISUALS` → same BE keying, no re-init),
 * same `PfV2StagingVisual` projection shape, same provider mount point, same
 * exported hook/provider names → all consumers and tests are unchanged apart
 * from the import path.
 *
 * Transport moved to the v2 session-file surface: the mint and the
 * delete are addressed by `productCode` rather than a legacy numeric product id,
 * so no wire value is parsed anywhere in this path. The RAW_WEB derivative poll
 * still rides its v1 endpoint — it keys on session + filename + type and never
 * involves a product id, and the v2 download-url takes no variant parameter.
 *
 * Fully detached from the legacy `usePurchaseSessionVisualStore` /
 * `usePurchaseSessionVisualOperations`: no shared state, no S1→S2 session
 * aliasing. Session id + admin email are read live from the PF-v2 store inside
 * the factory's resolver context.
 */

import { useCallback } from 'react'

import { FileAction, FileState, type FileAPIEntry } from 'components/common/FileAPI'
import { VisualFileType } from 'constants/visual'

import { SessionFileKind } from 'models/purchaseFlowRevamp'

import { createFileHubKind } from '../createFileHubKind'
import { makeSessionFileDeleteHandler, makeSessionFileUploadResolver } from '../sessionFiles.transport'
import type { FileHubBaseMetadata, FileHubKind } from '../fileHub.types'

// ─── Staging-specific transport seams ─────────────────────────────────────────

/**
 * Single, session-agnostic FileAPI scope for all PF-v2 staging visuals.
 *
 * A static string (not keyed per-session) is correct here: PF v2 has exactly
 * one draft/session alive at a time, the provider that owns this scope is
 * mounted once at the PF-v2 root (`PurchaseFlowRevamp.page`), and the session id
 * is read live inside the resolvers. Keeping it static also avoids the
 * destructive `INIT_SCOPE` re-firing if the scope string changed mid-flight.
 */
export const PF_V2_STAGING_VISUAL_SCOPE = 'PF_V2_STAGING_VISUALS'

/**
 * Sentinel product ID staging visuals are grouped under. The drawer's
 * `FileUploadSection` passes it as `uploadProductId` →
 * `usePfV2VisualUpload().uploadVisuals(files, productId, productCode)`, and the
 * projection stamps it back onto each `PfV2StagingVisual.productId` (the grouping
 * key the native `useStagingRoomSync` reads). A low-collision constant (`1`) —
 * real staging products key from much higher legacy integers.
 *
 * Relocated here from the deleted `adapters/staging/StagingAdapter.component.tsx`
 * on the Wave-3 native rewrite (it is a file-hub upload concern, not an adapter one).
 */
export const STAGING_SENTINEL_PRODUCT_ID = 1

/** Scope-local FileAPI metadata for a PF-v2 staging visual entry. */
export interface PfV2VisualMetadata extends FileHubBaseMetadata {
  /** Legacy numeric product id the visual is keyed under (the staging sentinel). */
  productId: number
  /** The cart line that started the upload. Absent for files restored from a draft. */
  ownerId?: string
}

// ─── Projection ────────────────────────────────────────────────────────────

/** A staging visual ready to seed a room — the projection `useStagingRoomFeed`
 *  consumes. `id` is the GCS filename (= the room key legacy used). */
export interface PfV2StagingVisual {
  /** GCS filename — the room key legacy used, and the delete/download key. */
  id: string
  /** The product GROUP this file was uploaded for (the entry tag). The scope is
   *  shared for the whole PF-v2 lifetime, so this is what separates one product's
   *  files from another's — see `resolveTag`. */
  productCode: string | null
  productId: number
  webUrl: string | null
  /** The user-facing filename (as dropped), for attachment/display metadata. */
  originalName: string
}

export interface StagingVisualsProjection {
  visuals: PfV2StagingVisual[]
  /** Nothing uploading, nothing still polling a derivative, and ≥1 ready. */
  uploadFinished: boolean
  /** An upload is transferring OR a derivative is still polling. */
  isAnyRunning: boolean
}

/**
 * Derived read model off `allFilesArray`:
 *   - `visuals`        — RAW_WEB-ready entries, projected to `{id, productId, webUrl}`.
 *   - `uploadFinished` — nothing uploading, nothing still polling a derivative,
 *                        and ≥1 ready (mirrors legacy `uploadFinished` semantics,
 *                        which gated on RAW_WEB presence with no in-flight uploads).
 *   - `isAnyRunning`   — an upload is transferring OR a derivative is still polling.
 */
export function projectStagingVisuals(entries: Array<FileAPIEntry<PfV2VisualMetadata>>): StagingVisualsProjection {
  const relevant = entries.filter((e) => e.action !== FileAction.DELETE)
  const anyUploading = relevant.some((e) => e.state === FileState.RUNNING)
  const uploaded = relevant.filter(
    (e) => e.state === FileState.SUCCESS && (e.action === FileAction.UPLOAD || e.action === FileAction.RE_UPLOAD)
  )
  const anyPolling = uploaded.some((e) => !e.metadata?.derivativeResolved)
  const ready = uploaded.filter((e) => e.metadata?.derivativeReady)

  return {
    visuals: ready.map<PfV2StagingVisual>((e) => ({
      id: e.gcFilename ?? e.id,
      productCode: e.tag ?? null,
      // v2 has no numeric product id — `productCode` is the identity. Kept as the
      // sentinel because the legacy staging-room shape carries the field and
      // checks it for truthiness.
      productId: STAGING_SENTINEL_PRODUCT_ID,
      webUrl: e.displayUrl ?? null,
      originalName: e.originalFilename ?? e.gcFilename ?? e.id,
    })),
    uploadFinished: !anyUploading && !anyPolling && ready.length > 0,
    isAnyRunning: anyUploading || anyPolling,
  }
}

// ─── Kind descriptor ─────────────────────────────────────────────────────────

/** Params the drawer passes on upload.
 *
 *  `productCode` is the SELECTED VARIANT's code and goes to the BE verbatim.
 *  `scopeKey` is the grouping key for the shared FileAPI scope and is the
 *  PRODUCT-GROUP (shell) code, deliberately NOT the variant: a client who uploads
 *  under "Basic editing" and then switches to "Advanced" must keep seeing their
 *  files, and the line they commit must carry them. Keying on the variant hid the
 *  files on every variant switch and would have committed a line with none. */
export interface StagingUploadParams {
  productId: number
  productCode: string | null
  scopeKey?: string | null
  ownerId?: string
}

export const STAGING_VISUALS_KIND: FileHubKind<PfV2VisualMetadata, StagingVisualsProjection, StagingUploadParams> = {
  scope: PF_V2_STAGING_VISUAL_SCOPE,
  derivatives: [VisualFileType.RAW_WEB],
  makeUploadResolver: (ctx, params) =>
    makeSessionFileUploadResolver<PfV2VisualMetadata>(ctx, {
      productCode: params.productCode,
      kind: SessionFileKind.VISUAL,
    }),
  makeDeleteHandler: (ctx) => makeSessionFileDeleteHandler<PfV2VisualMetadata>(ctx),
  // THE grouping key. It used to be the constant sentinel, which put every
  // product's uploads in one bucket: opening AI photo editing after professional
  // photo editing showed the other product's file, and — once `inputImages`
  // started shipping — could bind the wrong photo to an order line. The product
  // group separates them (see `scopeKey`); the variant code and then `productId`
  // remain fallbacks for a caller that has no group to give.
  resolveTag: (params) => params.scopeKey ?? params.productCode ?? String(params.productId),
  resolveUploadMetadata: (params) => (params.ownerId ? { ownerId: params.ownerId } : undefined),
  project: projectStagingVisuals,
}

// ─── Provider + hook (byte-identical public API to the old subsystem) ──────────

const [StagingVisualsProvider, useStagingVisuals] = createFileHubKind(STAGING_VISUALS_KIND)

/** The single owner of the PF-v2 staging-visual FileAPI scope. Mounted ONCE at
 *  the PF-v2 root (`PurchaseFlowRevamp.page`, wrapping `<Routes>`) so the scope
 *  is shared between the drawer (`FileUploadSection`) and the configuration step
 *  (`StagingAdapter` → `useStagingRoomFeed`). Lives the whole PF-v2 lifetime;
 *  the default `purgeOnDestroy` clears the scope on leaving `/purchase-flow-revamp/*`. */
export const PfV2VisualUploadProvider = StagingVisualsProvider

/**
 * Staging-visual upload hook — the byte-identical public surface the drawer,
 * room feed, and legacy thumbnail resolver consume. Thin re-map of the generic
 * `createFileHubKind` output to the historical staging names/signatures so the
 * consumer diff is a rename, not a rewrite.
 */
/** The entries / visuals uploaded FOR one product. The FileAPI scope spans the
 *  whole flow, so every consumer that speaks for a single product line must
 *  narrow it — reading the scope raw is what leaked files across products. */
export const forProductCode = <T extends { tag?: string | null }>(
  items: T[],
  productCode: string | null
): T[] => (productCode ? items.filter((item) => item.tag === productCode) : [])

/**
 * The files an upload scope actually holds for a product: every SUCCESSFULLY
 * UPLOADED entry, whether or not its RAW_WEB derivative has landed yet.
 *
 * `projectStagingVisuals` deliberately gates on `derivativeReady` because it
 * feeds thumbnails and staging rooms. A cart line's OWNERSHIP must not: the file
 * is on the BE the moment the upload succeeds, and a client who presses "Add to
 * cart" a second after dropping a photo would otherwise commit a line with no
 * attachments — which now also means an order with no `inputImages`, i.e. the
 * photo silently missing from the order. The derivative only decides whether a
 * thumbnail can be shown, so a null `signedUrl` is fine here.
 */
export const uploadedFilesForProductCode = <
  T extends { tag?: string | null; gcFilename?: string | null; originalFilename?: string | null; displayUrl?: string | null; state?: string; action?: string }
>(entries: T[], productCode: string | null) =>
  forProductCode(entries, productCode)
    .filter((e) => e.action !== FileAction.DELETE)
    .filter((e) => e.state === FileState.SUCCESS)
    .filter((e) => e.action === FileAction.UPLOAD || e.action === FileAction.RE_UPLOAD)
    .filter((e): e is T & { gcFilename: string } => Boolean(e.gcFilename))
    .map((e) => ({
      gcFilename: e.gcFilename,
      signedUrl: e.displayUrl ?? null,
      originalFilename: e.originalFilename ?? e.gcFilename,
    }))

/** Same narrowing for the projected visuals, which carry `productCode` rather
 *  than the raw entry tag. */
export const visualsForProductCode = (
  visuals: PfV2StagingVisual[],
  productCode: string | null
): PfV2StagingVisual[] => (productCode ? visuals.filter((v) => v.productCode === productCode) : [])

/** Whether an entry was uploaded by a cart line other than `cartItemId`. Entries without an owner belong to any line. */
export const isOwnedByOtherLine = (entry: { metadata?: { ownerId?: string } | null }, cartItemId: string): boolean =>
  !!entry.metadata?.ownerId && entry.metadata.ownerId !== cartItemId

export function usePfV2VisualUpload() {
  const { entries, upload, remove, seed, visuals, uploadFinished, isAnyRunning, seenThisSession } = useStagingVisuals()

  /** `productId` is the FE staging sentinel (the grouping key); `productCode` is
   *  the selected variant's catalogue code, sent to the BE exactly as received. */
  const uploadVisuals = useCallback(
    (files: File[] | FileList, productId: number, productCode: string | null, scopeKey?: string | null, ownerId?: string) =>
      upload(files, { productId, productCode, scopeKey, ownerId }),
    [upload]
  )

  const deleteVisual = useCallback((id: string) => remove(id), [remove])

  return {
    /** Raw FileAPI entries for the drawer's `FileAPIUpload` listing (real progress). */
    stagingEntries: entries,
    uploadVisuals,
    deleteVisual,
    /** Display-only re-add of files a resumed draft already owns, tagged so the
     *  per-product readers keep them. See `useResumedUploads`. */
    seedVisuals: seed,
    visuals,
    uploadFinished,
    isAnyRunning,
    /** Ids seen live in the staging feed this session (§10.2 membership prune). */
    seenThisSession,
  }
}
